import {
  ExcludeDurationOption,
  P21LeadTimeSource,
  P21SafetyStockType,
  RecurrencyReplenishmentMethod,
  TransferTargetReviewMethod,
} from '@recurrency/core-api-schema/dist/common/enums';
import { DailyWorkflowItemLocationDataDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowItemLocationData';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import moment from 'moment/moment';
import { ZipCelXSheet } from 'zipcelx';

import { notifyAndDownload, recordsToXSheet } from 'components/recipes/DownloadButton';

import { formatPercent, pluralize } from 'utils/formatting';
import { getAllRecordsFromSearchIndex } from 'utils/search/search';
import { SearchRequest } from 'utils/search/types';
import { MinMaxSubmissionType } from 'utils/track';

export const safetyStockSourceNames = {
  [P21SafetyStockType.classDays]: 'ERP Purchase Class',
  [P21SafetyStockType.classServiceLevel]: 'ERP Purchase Class (Service Level)',
  [P21SafetyStockType.defaultDays]: 'Default',
  [P21SafetyStockType.itemDays]: 'ERP Item',
  [P21SafetyStockType.itemServiceLevel]: 'ERP Item (Service Level)',
  [P21SafetyStockType.overrideDays]: 'Override',
  [P21SafetyStockType.serviceLevel]: 'Default  (Service Level)',
  [P21SafetyStockType.supplierDays]: 'ERP Supplier',
  [P21SafetyStockType.supplierServiceLevel]: 'ERP Supplier (Service Level)',
};

export const transferTargetReviewMethodNames: Record<TransferTargetReviewMethod, string> = {
  [TransferTargetReviewMethod.Cycle]: 'Cycle',
  [TransferTargetReviewMethod.Schedule]: 'Schedule',
};

export const leadTimeSourceNames: Record<P21LeadTimeSource, string> = {
  [P21LeadTimeSource.smart]: 'Smart',
  [P21LeadTimeSource.override]: 'Override',
};

const planningKeys = [
  'item_uid',
  'item_id',
  'item_name',
  'location',
  'lead_time',
  'lead_time_source',
  'replenishment_method',
  'replenishment_location',
  'unit_of_measure',
  'planning_status',
  'primary_supplier',
  'smart_lead_time',
  'safety_stock_type',
  'safety_stock_value',
  'predictability_tag',
  'demand_pattern',
  'min_order_quantity',
  'recommended_min',
  'recommended_max',
  'current_min',
  'current_max',
  'excluded',
  'excluded_until',
  'min_stock_floor',
  'raw_recommended_max',
  'recommended_max_pre_separation',
  'recommended_max_pre_shelf_life',
  'max_shelf_life_days',
  'max_shelf_life_stock_cap',
  'shelf_life_capped',
  'eoq',
  'annual_demand',
  'is_spoke',
  'lead_time_demand_qty',
  'avg_demand',
  'safety_stock_qty',
  'order_cost',
  'holding_cost',
  'recommended_min_prefloor',
  'sparse_months_worth_of_stock',
  'estimated_month_with_demand_qty',
  'm12_number_of_months_with_demand',
  'transfer_cycle_stock',
  'transfer_cycle_demand',
  'transfer_cycle_days',
  'transfer_review_method',
  'order_cycle_demand_qty',
  'daily_avg_forecast_demand_over_order_cycle',
  'order_cycle_days',
  'recurrency_replenishment_method',
  'unit_cost',
  'projected_inventory_change',
  'id',
  'recommended_max_prefloor',
  'recommended_min_source',
  'recommended_max_source',
] as const;
type PlanningItemKeys = typeof planningKeys[number];
export type PlanningItem = Pick<SearchPlanningDTO, PlanningItemKeys>;

/**
 * Maps Daily Workflow item-location data (including optional shelf-life
 * diagnostics) into a `PlanningItem` for equation popovers.
 */
export function convertDailyWorkflowDataToPlanningItem(
  dailyWorkflowData: DailyWorkflowItemLocationDataDTO,
): PlanningItem {
  return {
    item_uid: dailyWorkflowData.itemId,
    item_id: dailyWorkflowData.itemCode,
    location: dailyWorkflowData.location,
    lead_time: dailyWorkflowData.leadTime,
    safety_stock_value: dailyWorkflowData.safetyStock,
    min_order_quantity: dailyWorkflowData.minOrderQuantity,
    primary_supplier: dailyWorkflowData.primarySupplier,
    smart_lead_time: dailyWorkflowData.smartLeadTime,
    safety_stock_type: dailyWorkflowData.safetyStockType,
    lead_time_source: dailyWorkflowData.leadTimeSource,
    replenishment_location: dailyWorkflowData.replenishmentLocation,
    unit_of_measure: dailyWorkflowData.planningUnitOfMeasure,
    min_stock_floor: dailyWorkflowData.stockFloor,
    recommended_min: dailyWorkflowData.recommendedMin,
    recommended_max: dailyWorkflowData.recommendedMax,
    raw_recommended_max: dailyWorkflowData.rawRecommendedMax,
    recommended_max_pre_separation: dailyWorkflowData.recommendedMaxPreSeparation,
    recommended_max_pre_shelf_life: dailyWorkflowData.recommendedMaxPreShelfLife,
    max_shelf_life_days: dailyWorkflowData.maxShelfLifeDays,
    max_shelf_life_stock_cap: dailyWorkflowData.maxShelfLifeStockCap,
    shelf_life_capped: dailyWorkflowData.shelfLifeCapped,
    eoq: dailyWorkflowData.eoq,
    annual_demand: dailyWorkflowData.annualDemand,
    is_spoke: dailyWorkflowData.isSpoke,
    lead_time_demand_qty: dailyWorkflowData.leadTimeDemandQty,
    avg_demand: dailyWorkflowData.avgDemand,
    safety_stock_qty: dailyWorkflowData.safetyStockQty,
    order_cost: dailyWorkflowData.orderCost,
    holding_cost: dailyWorkflowData.holdingCost,
    recommended_min_prefloor: dailyWorkflowData.recommendedMinPrefloor,
    sparse_months_worth_of_stock: dailyWorkflowData.sparseMonthsWorthOfStock,
    estimated_month_with_demand_qty: dailyWorkflowData.estimatedMonthWithDemandQty,
    m12_number_of_months_with_demand: dailyWorkflowData.m12NumberOfMonthsWithDemand,
    transfer_cycle_stock: dailyWorkflowData.transferCycleStock,
    transfer_cycle_demand: dailyWorkflowData.transferCycleDemand,
    transfer_cycle_days: dailyWorkflowData.transferCycleDays,
    transfer_review_method: dailyWorkflowData.transferReviewMethod,
    order_cycle_demand_qty: dailyWorkflowData.orderCycleDemandQty,
    daily_avg_forecast_demand_over_order_cycle: dailyWorkflowData.dailyAvgForecastDemandOverOrderCycle,
    order_cycle_days: dailyWorkflowData.orderCycleDays,
    recurrency_replenishment_method: dailyWorkflowData.recurrencyReplenishmentMethod,
    demand_pattern: dailyWorkflowData.demandPattern,
    unit_cost: dailyWorkflowData.unitCost.toString(),
    current_min: dailyWorkflowData.currentMin,
    current_max: dailyWorkflowData.currentMax,
    id: dailyWorkflowData.itemId,
    recommended_min_source: dailyWorkflowData.recommendedMinSource,
    recommended_max_source: dailyWorkflowData.recommendedMaxSource,
  } as PlanningItem;
}

export interface ListItemsSelection {
  type: 'list';
  items: PlanningItem[];
  count: number;
}

export interface FilterItemsSelection {
  type: 'filter';
  query?: string;
  filters?: Obj<string[]>;
  sortBy?: SearchRequest['sortBy'];
  secondarySortBy?: SearchRequest['secondarySortBy'];
  /** This corresponds to the user-friendly title of the filter keys */
  filterTitleMapping?: Obj<string | undefined>;
  count: number;
}

export type MultiModalSelection = ListItemsSelection | FilterItemsSelection;

export type PlanningBulkUpdateType =
  | 'minMax'
  | 'safetyStock'
  | 'leadTime'
  | 'includeExclude'
  | 'flagged'
  | 'minStockFloor'
  | 'minOrderQuantity';

export interface MinMaxBulkUpdate {
  type: 'minMax';
  customMin: number | undefined;
  customMax: number | undefined;
  isOpOqVariant: boolean;
  submissionType: MinMaxSubmissionType;
}

export interface SafetyStockUpdate {
  type: 'safetyStock';
  safetyStock: number | undefined;
}

export interface LeadTimeUpdate {
  type: 'leadTime';
  leadTime: number | undefined;
}

export interface IncludeExcludeUpdate {
  type: 'includeExclude';
  exclude: boolean;
}

export interface FlaggedUpdate {
  type: 'flagged';
  flagged: boolean;
  comment: string;
}

export interface MinStockFloorUpdate {
  type: 'minStockFloor';
  minStockFloor: number | undefined;
}

export interface MinOrderQuantityUpdate {
  type: 'minOrderQuantity';
  minOrderQuantity: number | undefined;
}

export type PlanningBulkUpdate =
  | MinMaxBulkUpdate
  | SafetyStockUpdate
  | LeadTimeUpdate
  | IncludeExcludeUpdate
  | FlaggedUpdate
  | MinStockFloorUpdate
  | MinOrderQuantityUpdate;

export async function getPlanningListSelectionFromFilterSelection(
  selection: FilterItemsSelection,
  fieldsToRetrieve: Array<keyof SearchPlanningDTO> = ['item_uid', 'location'],
): Promise<ListItemsSelection> {
  // turn filter selection into list selection by fetching relevant records
  const items = await getAllRecordsFromSearchIndex<SearchPlanningDTO>({
    indexName: SearchIndexName.Planning,
    query: selection.query,
    filters: selection.filters,
    sortBy: selection.sortBy,
    secondarySortBy: selection.secondarySortBy,
    fieldsToRetrieve,
  });

  const listSelection: ListItemsSelection = {
    type: 'list',
    items,
    count: items.length,
  };

  return listSelection;
}

export type ExportValues = {
  exportedMin: number;
  exportedMax: number;
  exportedReplenishmentMethod: RecurrencyReplenishmentMethod;
  exportedSubmissionType: MinMaxSubmissionType;
};

export function isSafetyStockTypeServiceLevel(safetyStockType: P21SafetyStockType) {
  return /_SERVICE_LEVEL$/.test(safetyStockType);
}

// This is renamed due to a bug in esbuild-jest where importing any function with "ock" followed by "()" causes an error
// https://github.com/aelbore/esbuild-jest/issues/57
export function formatSafetyStockToStr(safetyStockValue: number | undefined, safetyStockType: P21SafetyStockType) {
  if (safetyStockValue === undefined) {
    return '-';
  }

  if (isSafetyStockTypeServiceLevel(safetyStockType)) {
    return formatPercent(safetyStockValue);
  }
  return pluralize(safetyStockValue, 'day', 'days', true);
}

export enum ExportOptions {
  Current = 'Current',
  CustomOpOq = 'Custom OP/OQ',
  CustomMinMax = 'Custom Min/Max',
  RecommendedOpOq = 'Recommended OP/OQ',
  RecommendedMinMax = 'Recommended Min/Max',
}

// This is used to keep track of the `Include` and `Exclude` type verbiage for the Item Location Setting Change Modal
export interface ItemLocationSettingChangeVerbiage {
  set: string;
  unset: string;
}

export const PlanningBulkUpdateToVerbiage: Record<PlanningBulkUpdateType, ItemLocationSettingChangeVerbiage> = {
  minMax: {
    set: 'Set',
    unset: 'Unset',
  },
  safetyStock: {
    set: 'Set',
    unset: 'Unset',
  },
  leadTime: {
    set: 'Set',
    unset: 'Unset',
  },
  includeExclude: {
    set: 'Exclude',
    unset: 'Include',
  },
  flagged: {
    set: 'Flag',
    unset: 'Resolve Flag',
  },
  minStockFloor: {
    set: 'Add',
    unset: 'Remove',
  },
  minOrderQuantity: {
    set: 'Set',
    unset: 'Unset',
  },
};

export const PlanningBulkUpdateToFields: Record<PlanningBulkUpdateType, string[]> = {
  minMax: ['min_stock', 'max_stock', 'replenishment_method'],
  safetyStock: ['safety_stock_days'],
  leadTime: ['lead_time'],
  includeExclude: ['excluded', 'excluded_until'],
  flagged: ['flagged', 'flag_reason'],
  minStockFloor: ['min_stock_floor'],
  minOrderQuantity: ['min_order_quantity'],
};

/**
 * Get the ISO date string for the given ExcludeDurationOption.
 * Date is calculated from the current date
 */
export const getIsoDateForExcludeDurationOption = (option: ExcludeDurationOption): string => {
  const getIsoDateFromToday = (amount?: moment.DurationInputArg1, unit?: moment.DurationInputArg2) =>
    moment().startOf('day').add(amount, unit).toISOString();

  switch (option) {
    case ExcludeDurationOption.OneDay:
      return getIsoDateFromToday(1, 'day');
    case ExcludeDurationOption.OneWeek:
      return getIsoDateFromToday(1, 'week');
    case ExcludeDurationOption.OneMonth:
      return getIsoDateFromToday(1, 'month');
    case ExcludeDurationOption.ThreeMonths:
      return getIsoDateFromToday(3, 'months');
    case ExcludeDurationOption.SixMonths:
      return getIsoDateFromToday(6, 'months');
    default:
      return '';
  }
};

export interface EclipseMinMaxUpdate {
  itemId: string;
  locationId: string;
  newMin: number;
  newMax: number;
}

/**
 * Eclipse min/max is not sent to the ERP. The sheet holds the keys of the Eclipse product_branch rows to update
 * (ProductID and BranchID) and the new OrderPoint (min) and LinePoint (max). Both Eclipse columns are integers.
 */
export function getEclipseMinMaxXSheet(updates: EclipseMinMaxUpdate[]): ZipCelXSheet {
  return recordsToXSheet(updates, [
    { title: 'ProductID', type: 'string', value: (update) => update.itemId },
    { title: 'BranchID', type: 'string', value: (update) => update.locationId },
    { title: 'OrderPoint', type: 'number', value: (update) => Math.round(update.newMin) },
    { title: 'LinePoint', type: 'number', value: (update) => Math.round(update.newMax) },
  ]);
}

export function downloadEclipseMinMaxExcel(updates: EclipseMinMaxUpdate[]): Promise<void> {
  return notifyAndDownload(async () => getEclipseMinMaxXSheet(updates), 'Eclipse_MinMax');
}
